import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { MessageSquare, Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const categories = [
  { value: "general_feedback", label: "General feedback" },
  { value: "feature_request", label: "Feature request" },
  { value: "bug_report", label: "Bug report" },
  { value: "ui_ux", label: "Design / usability" },
  { value: "performance", label: "Performance" },
];

export default function GlobalFeedbackButton({ user }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState(5);
  const [category, setCategory] = useState("general_feedback");
  const [message, setMessage] = useState("");

  if (!user) return null;

  const resetForm = () => {
    setRating(5);
    setCategory("general_feedback");
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Please write your feedback first");
      return;
    }

    setSaving(true);
    await base44.entities.Feedback.create({
      skipper_name: user.full_name || "Marstick user",
      skipper_email: user.email,
      rating,
      category,
      title: categories.find((item) => item.value === category)?.label || "Feedback",
      message: message.trim(),
      description: `Submitted from ${window.location.pathname}`,
      status: "new",
      is_public: false,
    });
    setSaving(false);
    setOpen(false);
    resetForm();
    toast.success("Thanks — your feedback was sent to the admin team");
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(8.75rem+env(safe-area-inset-bottom,0px))] left-3 z-40 h-12 rounded-full px-4 shadow-2xl shadow-primary/25 md:bottom-6 md:z-50 lg:left-60 max-[380px]:px-3 max-[380px]:text-xs"
      >
        <MessageSquare className="w-4 h-4" />
        Feedback
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Help us improve Marstick</DialogTitle>
            <DialogDescription>
              Share quick feedback with the admin team. It helps us improve the product.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-2">How was your experience?</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(value)}
                    className="h-10 w-10 rounded-xl hover:bg-primary/10 transition-colors"
                  >
                    <Star className={cn("w-6 h-6 mx-auto", value <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40")} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">Feedback type</p>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">Your feedback</p>
              <Textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Tell us what worked, what didn’t, or what you’d like to see next..."
                rows={5}
              />
              <Input value={window.location.pathname} readOnly className="mt-2 text-xs text-muted-foreground" />
            </div>

            <Button onClick={handleSubmit} disabled={saving} className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              Send feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}